import { useCallback, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgBootstrap } from '@/hooks/useOrganization';
import { useDismissOnOutsideClick } from '@/hooks/useDismissOnOutsideClick';
import { Icon } from '@/components/primitives/Icon';
import { Button } from '@/components/primitives/Button';
import { IconButton } from '@/components/primitives/IconButton';
import { Avatar } from '@/components/primitives/Avatar';
import { auth } from '@/core/firebase';
import { apiClient } from '@/core/api';
import { logClientError } from '@/core/clientLogger';
import { clearAllOrgSessions } from '@/core/sessionAuth';
import type { MeResponse } from '@/types/api';

export default function Header() {
  const { user } = useAuth();
  const { orgs, activeOrgId } = useOrgBootstrap();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = user?.displayName
    ? user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (user?.email?.slice(0, 2).toUpperCase() ?? '??');
  const activeOrg = orgs.find((org) => String(org.id) === String(activeOrgId)) ?? orgs[0];
  const orgName =
    activeOrg?.name ?? (activeOrgId ? `Organization #${activeOrgId}` : 'No organization');

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useDismissOnOutsideClick({
    isActive: menuOpen,
    onDismiss: closeMenu,
    containerRef: menuRef,
  });

  async function handleLogout() {
    try {
      const me = await apiClient<MeResponse>('/users/me');
      const orgIds = me.organizations.map((o) => o.id).filter((id): id is number => id != null);
      await clearAllOrgSessions(orgIds);
      await signOut(auth);
      closeMenu();
    } catch (err) {
      logClientError('Failed to sign out', err);
    }
  }

  return (
    <header className="aspectly:fixed aspectly:top-0 aspectly:right-0 aspectly:w-full aspectly:md:w-[calc(100%-16rem)] aspectly:h-16 aspectly:bg-white/80 aspectly:backdrop-blur-md aspectly:flex aspectly:justify-between aspectly:items-center aspectly:px-4 aspectly:md:px-8 aspectly:z-40 aspectly:border-b aspectly:border-stone-200/60">
      <div className="aspectly:flex aspectly:items-center aspectly:w-1/2">
        <div className="aspectly:relative aspectly:w-full aspectly:max-w-md aspectly:group">
          <Icon
            name="search"
            className="aspectly:absolute aspectly:left-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-outline-variant aspectly:group-focus-within:text-primary aspectly:transition-colors"
          />
          <input
            className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border-none aspectly:rounded-xl aspectly:pl-10 aspectly:pr-4 aspectly:py-2 aspectly:text-sm aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:placeholder:text-stone-400 aspectly:outline-hidden"
            placeholder="Search templates, formats, or assets..."
            type="text"
          />
        </div>
      </div>

      <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:md:gap-6">
        <div className="aspectly:hidden aspectly:md:flex aspectly:items-center aspectly:gap-2">
          <IconButton icon="notifications" variant="ghost" />
          <IconButton icon="settings" variant="ghost" />
        </div>

        <Button variant="primary" size="sm" className="aspectly:hidden aspectly:sm:flex aspectly:font-black aspectly:text-[10px]">
          <Icon name="arrow_upward" className="aspectly:text-[14px]" />
          Upgrade
        </Button>

        <div className="aspectly:hidden aspectly:sm:flex aspectly:items-center aspectly:gap-3 aspectly:bg-stone-50 aspectly:px-3 aspectly:py-1.5 aspectly:rounded-full aspectly:border aspectly:border-stone-100">
          <div className="aspectly:relative aspectly:flex aspectly:items-center aspectly:justify-center aspectly:w-8 aspectly:h-8">
            <svg className="aspectly:w-full aspectly:h-full aspectly:-rotate-90" viewBox="0 0 36 36">
              <circle
                className="aspectly:stroke-stone-200"
                cx="18"
                cy="18"
                fill="none"
                r="16"
                strokeWidth="3"
              />
              <circle
                className="aspectly:transition-all aspectly:duration-1000 aspectly:ease-out"
                cx="18"
                cy="18"
                fill="none"
                r="16"
                stroke="#8e84ad"
                strokeDasharray="100"
                strokeDashoffset="86.6"
                strokeLinecap="round"
                strokeWidth="3"
              />
            </svg>
            <span className="aspectly:absolute aspectly:text-[9px] aspectly:font-black aspectly:text-stone-600">13%</span>
          </div>
          <div className="aspectly:flex aspectly:flex-col">
            <span className="aspectly:text-[10px] aspectly:font-black aspectly:text-stone-800 aspectly:leading-none">10 / 75</span>
            <span className="aspectly:text-[8px] aspectly:uppercase aspectly:tracking-tighter aspectly:font-black aspectly:text-primary">
              Credits Left
            </span>
          </div>
        </div>

        <div className="aspectly:hidden aspectly:sm:block aspectly:h-8 aspectly:w-px aspectly:bg-stone-200" />

        <div ref={menuRef} className="aspectly:relative">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:cursor-pointer"
            aria-expanded={menuOpen}
          >
            <div className="aspectly:hidden aspectly:md:block aspectly:text-right">
              <p className="aspectly:text-xs aspectly:font-black aspectly:text-on-surface">{initials}</p>
              <p className="aspectly:text-[10px] aspectly:text-primary aspectly:uppercase aspectly:font-black aspectly:tracking-tighter">PRO</p>
            </div>
            <Avatar src={user?.photoURL} initials={initials} size="md" />
          </button>
          {menuOpen && (
            <div className="aspectly:absolute aspectly:right-0 aspectly:mt-3 aspectly:w-48 aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/20 aspectly:bg-surface-container-lowest aspectly:shadow-xl aspectly:py-2">
              <div className="aspectly:px-4 aspectly:pb-2 aspectly:border-b aspectly:border-outline-variant/10">
                <p className="aspectly:text-[9px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant">
                  Organization
                </p>
                <p className="aspectly:text-sm aspectly:font-bold aspectly:text-on-surface aspectly:truncate">{orgName}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="aspectly:w-full aspectly:px-4 aspectly:py-2 aspectly:text-left aspectly:text-sm aspectly:font-bold aspectly:text-on-surface aspectly:hover:bg-surface-container-low aspectly:transition-colors aspectly:cursor-pointer"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
